import { SelectionModel } from '@angular/cdk/collections';
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, Inject, OnDestroy, OnInit } from "@angular/core";
import { MAT_EXPANSION_PANEL_DEFAULT_OPTIONS } from '@angular/material/expansion';
import { ActivatedRoute, Router } from '@angular/router';
import { Evaluation, isPlant, Sample, SampleType, EvaluationService, Organization, CustomProtocolService, Status } from '@core/data';
import { Dialog, Snackbar } from '@core/material';
import { Select, Store } from '@ngxs/store';
import { Observable, Subject } from 'rxjs';
import { debounceTime, takeUntil } from 'rxjs/operators';
import { Library } from '@library';
import { LIBRARY } from '@app/evaluation/library';
import { CharacteristicImageGalleryDialog, CharacteristicImageGalleryDialogData } from '../evaluation-form/characteristic-image-gallery/characteristic-image-gallery.dialog';
import { ImageGalleryDialog, ImageGalleryDialogData } from '../evaluation-detail/image-gallery.dialog';
import { PrintEvaluationFormDialog, PrintEvaluationFormDialogData } from '../evaluation-detail/print-evaluation-form/print-evaluation-form.dialog';
import { CharacteristicsFormDialog } from '../evaluation-form/characteristcs-form/characteristics-form.dialog';
import { EvaluationCompleteFormDialog, EvaluationCompleteFormDialogData } from '../evaluation-form/evaluation-complete-form.dialog';
import { EvaluationFormDialog, EvaluationFormDialogData } from '../evaluation-form/evaluation-form.dialog';
import { EvaluationScheduleFormDialogData, EvaluationScheduleFormDialog } from '../evaluation-form/evaluation-schedule-form.dialog';
import { ImagesFormDialog, ImagesFormDialogData, ImageUploadType } from '../evaluation-form/images-form/images-form.dialog';
import { MeasurementsFormDialog } from '../evaluation-form/measurements-form/measurements-form.dialog';
import { NotesFormDialog } from '../evaluation-form/notes-form/notes-form.dialog';
import { SampleFormDialog, SampleFormDialogData } from '../sample-form/sample-form.dialog';
import { SampleDetailState } from './sample-detail.state';
import { InitSampleDetail, LoadSampleDetail, SetEvaluationSelected } from './sample-detail.state-actions';
import { SampleDetailStateModel } from './sample-detail.state-model';
import { SamplePrimaryImageFormDialogData, SamplePrimaryImageFormDialog } from '../sample-form/sample-primary-image-form.dialog';
import * as moment from 'moment';
import { AuthState } from '@app/auth';
import { ProtocolManagerState, ProtocolManagerStateModel } from '../protocol-manager/protocol-manager.state';
import { MatRadioChange } from '@angular/material/radio';
import { EvaluationTransferFormStateModel, EvaluationTransferState, InitEvaluationTransfer, SetelectedEvaluations } from '../evaluation-transfer/evaluation-transfer.view.state';
import { MarkStateForReload } from '../sample-index/sample-index.state-actions';


@Component({
    selector: 'pv-sample-detail-view',
    templateUrl: 'sample-detail.view.html',
    changeDetection: ChangeDetectionStrategy.OnPush,
    preserveWhitespaces: false,
    host: {
        class: 'pv-sample-detail-view'
    },
    providers: [
        {
            provide: MAT_EXPANSION_PANEL_DEFAULT_OPTIONS,
            useValue: {
                expandedHeight: '92px',
                collapsedHeight: '72px'
            }
        }
    ]
})
export class SampleDetailView implements OnInit, OnDestroy {

    @Select(AuthState.selectedOrg)
	selectedOrg$: Observable<Organization>;

    @Select(SampleDetailState)
    state$: Observable<SampleDetailStateModel>;

    @Select(ProtocolManagerState)
    protocols$: Observable<ProtocolManagerStateModel>;

    evalDetailExpansion = new SelectionModel<string>(true, []);

    now = moment();

    isGuest: boolean = false;
    isReadonly: boolean = false;
    sample: Sample;

    charImageCounts: { [evalKey: string]: number } = {};
    private _charImageCountsRequested: { [evalKey: string]: boolean } = {};
    private _hasImageCapableCharsCache: { [evalKey: string]: boolean } = {};

    selection = new SelectionModel<string>(true, []);

    private _destroy$ = new Subject();

    constructor(
        private _route: ActivatedRoute,
        private _router: Router,
        private _store: Store,
        private _dialog: Dialog,
        private _snackbar: Snackbar,
        private _evalService: EvaluationService,
        private _custom_protocolService: CustomProtocolService,
        @Inject(LIBRARY) private _library: Library,
        private _changeDetectorRef: ChangeDetectorRef,
    ) { }

    ngOnInit() {

        // setup state with route params
        this._route.paramMap
            .pipe(takeUntil(this._destroy$))
            .subscribe(params => {

                this.now = moment();

                this._store.dispatch(new InitSampleDetail(
                    params.get('orgKey'),
                    <SampleType>params.get('type'),
                    params.get('sampleKey')
                ));

            });

        this.selectedOrg$.pipe(takeUntil(this._destroy$))
            .subscribe(organisation => {
                this.isReadonly = organisation?.access.role === 'r';
                this.isGuest = organisation?.access.role === 'guest';
            });

        this.selection.changed
            .pipe(takeUntil(this._destroy$), debounceTime(100))
            .subscribe(() => {
                this._store.dispatch(new SetEvaluationSelected(this.selection.selected));
            });

        // Ensures that selection on UI always matches the state
        this.state$.subscribe(data => {
            if (data == null) {
                this.selection.clear();
                return;
            }

            this.sample = data.sample;

            (data.evaluations || []).forEach(evalu => this.loadCharImageCount(evalu));

            if (data.selectedKeys.length == 0) {
                this.selection.clear();
                return;
            }

            data.selectedKeys.forEach(key => this.selection.select(key));
        });

    }

    reload() {
        this._store.dispatch(new LoadSampleDetail);
    }

    ngOnDestroy() {
        this._destroy$.next();
        this._destroy$.complete();
    }

    editSample(sampleKey: string){

        this._dialog.open(SampleFormDialog, {data: {key: sampleKey}});

    }

    removeSample(sampleKey: string) {

        this._dialog.confirm(
            'Remove Sample',
            'Are you sure you want to remove this sample?',
            'Remove',
            'Cancel'
        ).afterClosed().subscribe(res => {
            if (res) {
                // this._store.dispatch(new DeleteSam)
                //     .pipe(takeUntil(this._destroy$))
                //     .subscribe(state => {
                //         this._snackbar.message("Sample Removed");
                //         this.back();
                //     });
            }
        });

    }

    setPrimaryImage(sampleKey: string) {

        const data: SamplePrimaryImageFormDialogData = { key: sampleKey };
        this._dialog.open(SamplePrimaryImageFormDialog, { data });
    }


    addEvaluation(sampleKey: string) {

        const state: SampleDetailStateModel = this._store.selectSnapshot(SampleDetailState);

        const data: EvaluationFormDialogData = {
            orgKey: state.orgKey,
            defaults: {
                sampleKey,
                massAction: false,
                cropId: state.sample.scionCultivar.cropId,
                sampleType: state.sample.type,
            }
        };

        this._dialog.open(EvaluationFormDialog, { data });

    }

    scheduleEvaluations(sampleKey: string, orgKey: string) {

        const data: EvaluationScheduleFormDialogData = {
            orgKey,
            sampleKey
        };

        this._dialog.open(EvaluationScheduleFormDialog, {data})
        .afterClosed()
        .subscribe(result => {
            if (!result) return;

            this.reload();
        });

    }

    editEvaluation(evalKey: string) {

        const state: SampleDetailStateModel = this._store.selectSnapshot(SampleDetailState);

        const data: EvaluationFormDialogData = {
            key: evalKey,
            orgKey: state.orgKey,
            defaults: {
                massAction: false,
                cropId: state.sample.scionCultivar.cropId,
                sampleType: state.sample.type,
            }
        };

        this._dialog.open(EvaluationFormDialog, { data });
    }

    completeEvaluation(orgKey: string, evalu: Evaluation, sample: Sample){
        const data: EvaluationCompleteFormDialogData = {
            key: evalu.key,
            orgKey: orgKey,
            defaults: {
                sampleKey: evalu.sampleKey,
                cropId: sample.scionCultivar?.cropId || null,
                massAction: false,
                sampleType: sample.type,
                sampleCodes: [],
                sampleKeys: [],
            }
        };

        this._dialog.open(EvaluationCompleteFormDialog, {data});
    }

    deleteEvaluation(evalKey: string) {

        this._dialog.confirm(
            'Remove Evaluation',
            'Are you sure you want to remove this evaluation?',
            'Remove',
            'Cancel'
        ).afterClosed().subscribe(res => {
            if (!res) return;

            this._evalService.delete(evalKey)
            .subscribe(res => {
                this._snackbar.message('Evaluation deleted successfully');
            });

        });

    }

    editCharacteristics(evalKey: string) {
        this._dialog.openFullscreen(CharacteristicsFormDialog, { data: { evalKey } });
    }

    editMeasurements(evalKey: string) {
        this._dialog.openFullscreen(MeasurementsFormDialog, { data: { evalKey } });
    }

    editNotes(evalKey: string) {
        this._dialog.openFullscreen(NotesFormDialog, { data: { evalKey } });
    }

    editImages(evalKey: string){

        const state: SampleDetailStateModel = this._store.selectSnapshot(SampleDetailState);

        const data: ImagesFormDialogData = {
            key: evalKey,
            type: ImageUploadType.SAMPLE_EVALUATION,
            sampleKey: state.sampleKey,
            orgKey: state.orgKey,
            sampleType: state.sampleType,
            allEvaluations: state.evaluations,
        };

        this._dialog.openFullscreen(ImagesFormDialog, { data });
    }

    hasImageCapableChars(evalu: Evaluation): boolean {

        if(!evalu || !evalu.key) return false;

        if(this._hasImageCapableCharsCache[evalu.key] !== undefined){
            return this._hasImageCapableCharsCache[evalu.key];
        }

        const result = this.computeHasImageCapableChars(evalu);
        this._hasImageCapableCharsCache[evalu.key] = result;
        return result;
    }

    private computeHasImageCapableChars(evalu: Evaluation): boolean {

        if(!evalu || !evalu.protocolId) return false;

        const protocol = this._custom_protocolService.allProtocols().get(evalu.protocolId);

        if(!protocol) return false;

        return protocol.chars
            .map(charId => this._library.chars.get(charId))
            .filter(char => !!char)
            .some(char => char.imageAnalysis === true);
    }

    openCharacteristicImageGallery(evalu: Evaluation){

        const state: SampleDetailStateModel = this._store.selectSnapshot(SampleDetailState);

        if (!state.sample) return;

        let data: CharacteristicImageGalleryDialogData = {
            evalKey: evalu.key,
            protocolId: evalu.protocolId,
            sampleKey: state.sample.key,
            orgKey: state.orgKey,
            sampleType: state.sample.type,
            evaluations: state.evaluations || [],
        };

        this._dialog.openFullscreen(CharacteristicImageGalleryDialog, { data })
            .afterClosed()
            .pipe(takeUntil(this._destroy$))
            .subscribe(() => {
                this._charImageCountsRequested[evalu.key] = false;
                this.loadCharImageCount(evalu);
            });
    }

    private loadCharImageCount(evalu: Evaluation){

        if(!evalu || !evalu.key) return;
        if(this._charImageCountsRequested[evalu.key]) return;
        if(!this.hasImageCapableChars(evalu)) return;

        this._charImageCountsRequested[evalu.key] = true;

        this._evalService.getCharacteristicImages(evalu.key)
            .pipe(takeUntil(this._destroy$))
            .subscribe(images => {

                if(!Array.isArray(images)) return;

                this.charImageCounts = {
                    ...this.charImageCounts,
                    [evalu.key]: images.length
                };
                this._changeDetectorRef.markForCheck();
            });

    }

    viewImageGallery(evalu: Evaluation){
        let data: ImageGalleryDialogData = {
            images: evalu.images,
            currentIndex: 0,
        };

        this._dialog.openFullscreen(ImageGalleryDialog, {data, disableClose: false}, false);
    }

    printEvaluationForm(evalKey: string) {

        const data: PrintEvaluationFormDialogData = {
            evalKey
        };

        this._dialog.open(PrintEvaluationFormDialog, { data });
    }


    extract(sample: Sample){

        if(!isPlant(sample)) return;

        const data: SampleFormDialogData = {
            defaults: {
                type: SampleType.HARVEST,
                plantSample: sample,
                ownerOrgKey: sample.ownerOrgKey
            }
        };

        this._dialog.open(SampleFormDialog, {data})
            .afterClosed()
            .subscribe(sample => {
                if(sample){
                    this._router.navigate([
                        '/org', sample.ownerOrgKey,
                        'samples', sample.type, sample.key
                    ]);
                }
            });

    }

    toggleDetail(evalKey: string) {
        this.evalDetailExpansion.toggle(evalKey);
    }

    trackEvaluation(index, item) {
        return item.key;
    }

    trackById(index, item){
        return item.id;
    }

    copyCode(evalCode: string) {
        navigator.clipboard.writeText(evalCode).then(() => {
            this._snackbar.info('Evaluation code copied to cliboard.');
        }).catch(err => {
            console.error('Copy Error', err);
            this._snackbar.error('Error while copying evaluation code');
        });
    }

    openTransferDialog(sample: Sample) {

        this._store.dispatch(new SetelectedEvaluations(this.selection.selected || []));

        this._router.navigate([
            '/org',
            sample.ownerOrgKey,
            'eval-transfer',
            sample.key
        ]);
    }

    returnToIndex(orgKey: string, type: SampleType) {
        this._store.dispatch(new MarkStateForReload);

        this._router.navigate([
            '/org',
            orgKey,
            'samples',
            type,
        ]);
    }

}